import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, allContent } from '@/mocks/siteData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory) || categories[0];
  const filteredContent = allContent.filter((item) => item.category === activeCategory);

  const handleCategoryChange = (newSlug: string) => {
    setActiveCategory(newSlug);
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const catNamesMap: Record<string, string> = {};
  categories.forEach((c) => { catNamesMap[c.slug] = c.name; });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `在线观看${currentCategory.name} - 极速高清`,
    description: currentCategory.description,
    url: `https://movihubs.github.io/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '极速高清',
      url: 'https://movihubs.github.io',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: currentCategory.name },
              ]}
            />
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-3">
              在线观看{currentCategory.name}
            </h1>
            <p className="text-foreground-500 text-sm mt-2">{currentCategory.description}</p>
          </div>
        </div>

        <div className="border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 py-3 overflow-x-auto flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          {filteredContent.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredContent.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={item.image}
                      alt={`在线观看${item.title}`}
                      title={`在线观看${item.title} - 极速高清`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-black/60 backdrop-blur-sm text-white text-xs rounded-md">
                        {item.categoryName}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="flex items-center gap-1 px-2 py-1 bg-black/60 backdrop-blur-sm text-secondary-400 text-xs rounded-md">
                        <i className="ri-star-fill text-xs"></i>
                        {item.rating}
                      </span>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                      <div className="w-14 h-14 flex items-center justify-center bg-primary-500/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="ri-play-fill text-white text-2xl ml-0.5"></i>
                      </div>
                    </div>
                    {item.isNew && (
                      <div className="absolute bottom-3 left-3">
                        <span className="px-2 py-0.5 bg-accent-500 text-white text-xs rounded-md font-medium">NEW</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{item.year}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{item.duration}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                      <span>{item.views.toLocaleString()}次观看</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-inbox-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-foreground-600 mb-2">暂无内容</h3>
              <p className="text-sm text-foreground-400">该分类下暂无内容，请浏览其他分类</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}